import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightLeft, Building2, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { adminService } from '../services/admin-service';
import { empresasElegibles } from '../lib/empresas-elegibles';
import type { MembresiaDePersona } from '../types/admin.types';

/**
 * L5 (plan 335 · 1.2): a qué empresas entra una persona, y dar, cambiar, quitar o mover esos
 * accesos. Sólo la plataforma: el admin de una empresa no puede sumar a alguien de otra.
 *
 * Una misma cuenta (un correo, una contraseña) puede entrar a varias empresas, con un rol
 * distinto en cada una. Quitar un acceso lo APAGA: su historial en esa empresa queda y se puede
 * volver a dar. El cargo (rol personalizado) de cada empresa lo asigna esa empresa, en
 * Mi empresa › Usuarios.
 */

const ROLES = [
  { valor: 'SALESPERSON', etiqueta: 'Vendedor' },
  { valor: 'APPROVER', etiqueta: 'Aprobador' },
  { valor: 'COMPANY_ADMIN', etiqueta: 'Admin de empresa' },
] as const;

const etiquetaDeRol = (rol: string) => ROLES.find((r) => r.valor === rol)?.etiqueta ?? rol;

const MOTIVOS_DE_APAGADO = { QUITADO: 'lo quitó Prixia', MUDANZA: 'se mudó de empresa' } as const;

type Pedido = { tipo: 'dar' } | { tipo: 'mover' } | null;

interface Props {
  userId: string;
  nombre: string;
  membresias: MembresiaDePersona[];
  /** Vuelve a leer la ficha después de cada cambio. */
  onCambio: () => void | Promise<void>;
}

export function AccesosDeLaPersona({ userId, nombre, membresias, onCambio }: Props) {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [pedido, setPedido] = useState<Pedido>(null);
  const [empresas, setEmpresas] = useState<Array<{ id: string; name: string }>>([]);
  const [empresaElegida, setEmpresaElegida] = useState('');
  const [rolElegido, setRolElegido] = useState<string>('SALESPERSON');
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  // Las empresas se piden recién al abrir un diálogo: la ficha no las necesita.
  useEffect(() => {
    if (!pedido || empresas.length > 0) return;
    adminService
      .getTenants({ limit: 500 })
      // La lista de plataforma ya saca las demos archivadas; el selector las trata igual a todas.
      .then((r) => setEmpresas(empresasElegibles(r.data)))
      .catch(() => setEmpresas([]));
  }, [pedido, empresas.length]);

  // Decisión de Sebastián: advertir, no forzar. El aviso se ve antes de confirmar.
  useEffect(() => {
    setAviso(null);
    if (!pedido || !empresaElegida) return;
    let vigente = true;
    adminService
      .avisoDeClave(userId, empresaElegida)
      .then((r) => vigente && setAviso(r.warning))
      .catch(() => undefined);
    return () => {
      vigente = false;
    };
  }, [pedido, empresaElegida, userId]);

  const conAcceso = new Set(membresias.filter((m) => m.isActive).map((m) => m.tenant.id));
  const origen = membresias.find((m) => m.esOrigen);
  const elegibles = empresas.filter((e) =>
    pedido?.tipo === 'mover' ? e.id !== origen?.tenant.id : !conAcceso.has(e.id)
  );

  async function correr(accion: () => Promise<unknown>, ok: string) {
    setGuardando(true);
    try {
      await accion();
      toast({ title: ok });
      await onCambio();
      return true;
    } catch (e: any) {
      toast({
        title: 'No se pudo',
        description: e?.response?.data?.error ?? e?.message ?? 'Probá de nuevo.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setGuardando(false);
    }
  }

  function abrir(tipo: 'dar' | 'mover') {
    setEmpresaElegida('');
    setRolElegido('SALESPERSON');
    setPedido({ tipo });
  }

  async function confirmarPedido() {
    if (!pedido || !empresaElegida) return;
    const datos = { tenantId: empresaElegida, role: rolElegido };
    const listo =
      pedido.tipo === 'dar'
        ? await correr(() => adminService.darAcceso(userId, datos), 'Acceso dado')
        : await correr(() => adminService.moverDeEmpresa(userId, datos), 'Persona movida');
    if (listo) setPedido(null);
  }

  async function devolver(m: MembresiaDePersona) {
    const encender = () =>
      correr(
        () => adminService.cambiarAcceso(userId, m.tenant.id, { isActive: true }),
        'Acceso devuelto'
      );
    const { warning } = await adminService
      .avisoDeClave(userId, m.tenant.id)
      .catch(() => ({ warning: null }));
    if (!warning) return void encender();
    confirm({
      title: `Devolverle a ${nombre} el acceso a ${m.tenant.name}`,
      description: warning,
      confirmText: 'Volver a dar acceso',
      onConfirm: async () => {
        await encender();
      },
    });
  }

  function quitar(m: MembresiaDePersona) {
    confirm({
      title: `Quitarle a ${nombre} el acceso a ${m.tenant.name}`,
      description: m.isActive
        ? 'Deja de poder entrar a esa empresa y se cierran sus sesiones abiertas. Sus otras empresas siguen. Su historial ahí queda. Sólo Prixia le puede devolver este acceso.'
        : 'Hoy no entra a esa empresa, pero su administrador podría volver a encender el acceso. Al quitarlo, sólo Prixia se lo puede devolver, y esa empresa deja de contar al cambiarle la contraseña o el nombre.',
      confirmText: 'Quitar acceso',
      variant: 'destructive',
      onConfirm: async () => {
        await correr(() => adminService.quitarAcceso(userId, m.tenant.id), 'Acceso quitado');
      },
    });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-lg">Empresas a las que entra</CardTitle>
          <p className="text-sm text-muted-foreground">
            Con la misma cuenta, un rol en cada empresa. Quitar un acceso no borra nada.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => abrir('dar')}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Dar acceso a otra empresa
          </Button>
          <Button variant="outline" size="sm" onClick={() => abrir('mover')}>
            <ArrowRightLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Mover de empresa
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {membresias.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No entra a ninguna empresa.
          </p>
        ) : (
          <ul className="divide-y">
            {membresias.map((m) => (
              <li
                key={m.tenant.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
                data-testid={`acceso-${m.tenant.slug}`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Building2
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <Link
                      to={`/plataforma/empresas/${m.tenant.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {m.tenant.name}
                    </Link>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      {m.esOrigen && <Badge variant="outline">Su empresa</Badge>}
                      {m.customRole && <Badge variant="secondary">{m.customRole.name}</Badge>}
                      {m.isActive ? (
                        <span className="text-green-600">Con acceso</span>
                      ) : (
                        <span className="text-destructive">
                          Sin acceso
                          {m.apagadaPor ? ` · ${MOTIVOS_DE_APAGADO[m.apagadaPor]}` : ''}
                        </span>
                      )}
                      {!m.tenant.isActive && (
                        <span className="text-muted-foreground">· empresa desactivada</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={m.role}
                    disabled={guardando}
                    onValueChange={(role) =>
                      correr(
                        () => adminService.cambiarAcceso(userId, m.tenant.id, { role }),
                        'Rol cambiado'
                      )
                    }
                  >
                    <SelectTrigger className="h-8 w-44" aria-label={`Rol en ${m.tenant.name}`}>
                      <SelectValue>{etiquetaDeRol(m.role)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.valor} value={r.valor}>
                          {r.etiqueta}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* «Quitar» sólo tiene sentido con acceso: uno que ya está en «Sin acceso» se devuelve. */}
                  {m.isActive && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={guardando}
                      onClick={() => quitar(m)}
                      className="border-destructive/50 text-destructive hover:bg-destructive/10"
                    >
                      Quitar acceso
                    </Button>
                  )}
                  {!m.isActive && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={guardando}
                      onClick={() => devolver(m)}
                    >
                      Volver a dar acceso
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={pedido !== null} onOpenChange={(abierto) => !abierto && setPedido(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pedido?.tipo === 'mover'
                ? `Mover a ${nombre} de empresa`
                : `Dar a ${nombre} acceso a otra empresa`}
            </DialogTitle>
            <DialogDescription>
              {pedido?.tipo === 'mover'
                ? `La empresa elegida pasa a ser la suya y deja de entrar a ${origen?.tenant.name ?? 'la actual'}. Sus otros accesos siguen. Se cierran sus sesiones abiertas.`
                : 'Entra con su misma cuenta y su misma contraseña, con el rol que elijas en esa empresa.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="acceso-empresa">Empresa</Label>
              <Select value={empresaElegida} onValueChange={setEmpresaElegida}>
                <SelectTrigger id="acceso-empresa">
                  <SelectValue placeholder="Elegí una empresa" />
                </SelectTrigger>
                <SelectContent>
                  {elegibles.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acceso-rol">Rol en esa empresa</Label>
              <Select value={rolElegido} onValueChange={setRolElegido}>
                <SelectTrigger id="acceso-rol">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.valor} value={r.valor}>
                      {r.etiqueta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {aviso && (
              <p
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
              >
                {aviso}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPedido(null)}>
              Cancelar
            </Button>
            <Button disabled={!empresaElegida || guardando} onClick={confirmarPedido}>
              {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {pedido?.tipo === 'mover' ? 'Mover' : 'Dar acceso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </Card>
  );
}
